import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import '../Widget.scss';
import './EtaWidget.scss';
import { OrdersEtaMetrics } from '../../../../fetch/ordersEtaTypes';
import { fetchOrderEtaMetrics } from '../../../../fetch/fetchOrderEtaMetrics';
import { classNames } from '../../../../utils/classUtils';
import Button from '../../../../components/buttons/Button';
import { CalendarInputComponent } from '../../../../ReusableComponents/CalendarInput/CalendarInputComponent';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';

const formatTime = (minutes: number): string => {
  if (minutes > 0 && minutes % 60 === 0) {
    const h = Math.floor(minutes / 60);
    return h + (h === 1 ? ' hour' : ' hours');
  }
  return minutes + (minutes === 1 ? ' minute' : ' minutes');
};

const formatPercent = (value: number): string => {
  if (value < 0) {
    return '0%';
  }
  return `${Math.floor(value * 100)}%`;
};

const percentageFrom = (part: string, etaMetric: OrdersEtaMetrics): number => {
  return etaMetric && etaMetric.ordersDelivered > 0
    ? etaMetric[part] / etaMetric.ordersDelivered
    : 0;
};

export default function EtaWidget(): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [etaMetrics, setEtaMetrics] = useState<OrdersEtaMetrics | null>(null);
  const [date, setDate] = useState<string>(
    dayjs().subtract(1, 'day').format('YYYY-MM-DD')
  );

  const setDateCallback = useCallback(
    (dateParam: string) => setDate(dateParam),
    []
  );

  useEffect(() => {
    const fetchOrdersEtaMetrics = async () => {
      setIsLoading(true);
      try {
        const metrics = await fetchOrderEtaMetrics(dayjs(date));
        setEtaMetrics(metrics);
      } catch (e) {
        console.warn('Error fetching eta metrics', e);
        setEtaMetrics(null);
      }
      setIsLoading(false);
    };
    if (!collapsed) {
      fetchOrdersEtaMetrics();
    }
  }, [date, collapsed]);

  const etaRanges = etaMetrics?.etaRanges || [];
  const percentInWindow = percentageFrom('ordersDeliveredInWindow', etaMetrics);

  const percentPickupTooEarly = percentageFrom('pickupTooEarly', etaMetrics);
  const percentPickupTooLate = percentageFrom('pickupTooLate', etaMetrics);
  const percentDeliveryTooEarly = percentageFrom(
    'deliveryTooEarly',
    etaMetrics
  );
  const percentDeliveryTooLate = percentageFrom('deliveryTooLate', etaMetrics);
  const percentOrdersCloseToEtaRelativeToPickup = percentageFrom(
    'ordersCloseToEtaRelativeToPickup',
    etaMetrics
  );
  const percentOrdersCloseToEta = percentageFrom(
    'ordersCloseToEta',
    etaMetrics
  );

  return (
    <div className="widget">
      <div className="widget__header">
        <h5>ETA vs delivery time</h5>
        {collapsed && (
          <span className="widget__expand">
            <span>Click to expand</span>
            <Button
              title={<FaAngleDown />}
              onClick={() => {
                setCollapsed(false);
              }}
            />
          </span>
        )}
        {!collapsed && (
          <span className="widget__expand">
            <span>Click to collapse</span>
            <Button
              title={<FaAngleUp />}
              onClick={() => setCollapsed(true)}
            />{' '}
          </span>
        )}
      </div>
      {!collapsed && (
        <div>
          <div className="eta-metrics__title">
            <h6 className="widget-eta-metrics__header">
              <span>
                <em>Average ETA deviation: </em>
                {etaMetrics &&
                  etaMetrics.ordersDelivered > 0 &&
                  formatTime(Math.ceil(etaMetrics.etaDeviationMinutes.average))}
              </span>
            </h6>
            <div>
              <CalendarInputComponent
                className="eta-metrics__date-selector"
                date={date}
                setDate={setDateCallback}
                maxDate={dayjs().subtract(1, 'day').format('YYYY-MM-DD')}
              />
            </div>
          </div>

          <div style={{ width: '100%' }}>
            <table className="eta-metrics__table">
              <thead>
                <tr>
                  <th>Delivery time</th>
                  <th>Orders delivered</th>
                  <th colSpan={2}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {etaMetrics && (
                  <>
                    {etaRanges.map((etaRange, idx) => (
                      <tr
                        key={`eta-range-${etaRange.fromMinutes}-${etaRange.toMinutes}-${idx}`}
                      >
                        <td>
                          ETA ± {formatTime(etaRange.toMinutes)}
                          {etaRange.toMinutes === 15 ? ' (A)' : ''}
                        </td>
                        <td>{isLoading ? '…' : etaRange.count}</td>
                        <td>
                          {isLoading ? '…' : formatPercent(etaRange.percentage)}
                        </td>
                        <td>
                          <ProgressBar value={etaRange.percentage} />
                        </td>
                      </tr>
                    ))}
                    <tr key={`eta-range-in-delivery-window`}>
                      <td>Within delivery window</td>
                      <td>
                        {isLoading ? '…' : etaMetrics.ordersDeliveredInWindow}
                      </td>
                      <td>
                        {isLoading ? '…' : formatPercent(percentInWindow)}
                      </td>
                      <td>
                        <ProgressBar value={percentInWindow} />
                      </td>
                    </tr>
                    <tr key={`pickup-too-early`}>
                      <td>Pickup earlier than 15 minutes</td>
                      <td>{isLoading ? '…' : etaMetrics.pickupTooEarly}</td>
                      <td>
                        {isLoading ? '…' : formatPercent(percentPickupTooEarly)}
                      </td>
                      <td>
                        <ProgressBar value={percentPickupTooEarly} />
                      </td>
                    </tr>
                    <tr key={`pickup-too-late`}>
                      <td>Pickup later than 15 minutes</td>
                      <td>{isLoading ? '…' : etaMetrics.pickupTooLate}</td>
                      <td>
                        {isLoading ? '…' : formatPercent(percentPickupTooLate)}
                      </td>
                      <td>
                        <ProgressBar value={percentPickupTooLate} />
                      </td>
                    </tr>
                    <tr key={`delivery-too-early`}>
                      <td>Delivery earlier than 15 minutes</td>
                      <td>{isLoading ? '…' : etaMetrics.deliveryTooEarly}</td>
                      <td>
                        {isLoading
                          ? '…'
                          : formatPercent(percentDeliveryTooEarly)}
                      </td>
                      <td>
                        <ProgressBar value={percentDeliveryTooEarly} />
                      </td>
                    </tr>
                    <tr key={`delivery-too-late`}>
                      <td>Delivery later than 15 minutes</td>
                      <td>{isLoading ? '…' : etaMetrics.deliveryTooLate}</td>
                      <td>
                        {isLoading
                          ? '…'
                          : formatPercent(percentDeliveryTooLate)}
                      </td>
                      <td>
                        <ProgressBar value={percentDeliveryTooLate} />
                      </td>
                    </tr>
                    <tr key={`delivery-relative-to-pickup`}>
                      <td>ETA ± 15 minutes based on pickup (B)</td>
                      <td>
                        {isLoading
                          ? '…'
                          : etaMetrics.ordersCloseToEtaRelativeToPickup}
                      </td>
                      <td>
                        {isLoading
                          ? '…'
                          : formatPercent(
                              percentOrdersCloseToEtaRelativeToPickup
                            )}
                      </td>
                      <td>
                        <ProgressBar
                          value={percentOrdersCloseToEtaRelativeToPickup}
                        />
                      </td>
                    </tr>
                    <tr key={`union-delivery-relative-to-pickup-eta-range`}>
                      <td>Union of A and B</td>
                      <td>{isLoading ? '…' : etaMetrics.ordersCloseToEta}</td>
                      <td>
                        {isLoading
                          ? '…'
                          : formatPercent(percentOrdersCloseToEta)}
                      </td>
                      <td>
                        <ProgressBar value={percentOrdersCloseToEta} />
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const percentageColors = [
  '#81D0D9',
  '#73C4CD',
  '#66B8C2',
  '#59ACB7',
  '#4CA0AC',
  '#3F94A0',
  '#328895',
  '#257C8A',
  '#18707F',
  '#0B6574',
];

function percentColor(value: number): string {
  if (value >= 1) {
    return percentageColors[percentageColors.length - 1];
  }
  return percentageColors[Math.floor(value * percentageColors.length)];
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="eta-metrics__progress-bar-container">
      {value != null && (
        <>
          <div className="eta-metrics__progress-bar-wrapper">
            <div className="eta-metrics__progress-bar">
              <div
                className="eta-metrics__progress-bar-fill"
                style={{
                  width: `${Math.floor(value * 10000) / 100}%`,
                }}
              >
                <span
                  className={classNames(
                    {
                      'eta-metrics__progress-bar-fill_animate': value > 0,
                    },
                    'eta-metrics__progress-bar-fill_progress'
                  )}
                  style={{
                    backgroundColor: percentColor(value),
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
